"use client"
import { type CSSProperties, useLayoutEffect, useRef, useState } from "react"
import { type LucideIcon } from "lucide-react"
import { motion } from "motion/react"
import { type Policy, type PolicyStatus } from "./policiesData"
import {
  POLICY_LABEL_HOVER_COLOR,
  type PolicyItemAppearance,
} from "./policies-panel/policyAppearance"
import PolicyItem from "./PolicyItem"
import { usePolicyHoverSwap } from "./policyHoverSwap"
import { usePolicyLayoutAnimationDisabled } from "./policyLayoutAnimationContext"

const BOX_GAP = .5

type PoliciesSectionProps = {
  status: PolicyStatus
  label: string
  policies: Policy[]
  onHoverPolicy?: (policyId: string | null) => void
  // Fires when the pointer enters the status LABEL itself (not a box) — the
  // below-the-HUD detail slot lists every policy by section while a label is
  // hovered (see belowHudDetailSwap.tsx). Enter-only; the enclosing sticky
  // zone handles clearing.
  onHoverLabel?: () => void
  appearance: PolicyItemAppearance
  compact?: boolean
  reverse?: boolean
  // "columns": label stacked above a row of boxes (each section is one column of
  // the side-by-side panel). "rows": label on the left, boxes on the right, with
  // sections stacked vertically — used when the panel sits beside another block.
  layout?: "columns" | "rows"
  borderRadius?: number
  gap?: number
  strokeWidth?: number
  squareSize?: number
  // Narrows the box width only, keeping its height at squareSize/BOX_SIZE.
  boxWidth?: number
  // Overrides the "rows" layout label-gutter width (default ROWS_LABEL_WIDTH_PX).
  // Shrink it with shorter labels so more boxes fit per row.
  labelWidth?: number
  // Box outline shape forwarded to PolicyItem ("rounded" default, or "hexagon").
  shape?: "rounded" | "hexagon"
  // When true, the boxes WRAP onto multiple lines instead of overflowing — so a
  // crowded status never runs off the edge in a narrow column.
  wrapBoxes?: boolean
  // "rows" layout label placement: "left" (default gutter), "top" (label above
  // the boxes, which then get the full width), or "right" (boxes hug the column's
  // left edge and the label sits to their right).
  rowsLabelPosition?: "left" | "top" | "right"
  // "rows" + left label: drop the fixed-width gutter and let the label shrink to
  // its word, with the boxes starting ROWS_LABEL_HUG_GAP_PX after it (and filling
  // the rest of the row). Fits more boxes per line than the aligned gutter.
  labelHug?: boolean
  // In labelHug mode, which edge the boxes align to within the remaining row
  // width: "start" (default — boxes hug the label) or "end" (boxes right-align to
  // the column's right edge, with the label hugging its word on the left).
  labelHugAlign?: "start" | "end"
  // When set, this icon replaces the text label (e.g. a status glyph), so the
  // label gutter can shrink right down. Drawn at the label's text color.
  statusIcon?: LucideIcon
  // When false, the status label keeps its given casing instead of the default
  // ALL-CAPS (so a variant can show e.g. "Current Policy" mixed-case).
  labelUppercase?: boolean
  // Overrides the status label font size (default 9px), e.g. "8.5px".
  labelFontSize?: string
  // In labelHug mode, overrides the gap (px) between the label word and the first
  // box (default ROWS_LABEL_HUG_GAP_PX = 10). HUD 75 tightens this to 6.
  labelHugGapPx?: number
  // When the boxes outgrow their row, let the row SHRINK (min-width: 0) so the
  // right-aligned overflow slides left OVER the label — staying inside the
  // column — instead of pushing the section past the column's right edge.
  // Pair with labelHugGapPx={0}. See HUD 86.
  overlapLabel?: boolean
  // Shorter fallback label, shown INSTEAD of `label` whenever the full label +
  // boxes would overflow the row (measured live against the column width). Pair
  // with overlapLabel so a row that's crowded even at the short label keeps its
  // overflow inside the column. See HUD 87.
  crowdedLabel?: string
  // Truncate the label CHARACTER BY CHARACTER as the boxes intrude: every
  // character the squares would touch or hide is dropped (e.g. "Current P"),
  // and characters re-appear as boxes move to other statuses. Relies on the
  // label's mono font for uniform character width. Pair with overlapLabel.
  // See HUD 88.
  truncateLabelOnOverlap?: boolean
  fillPatternStyle?: CSSProperties
  // Drop the status glyph inside each box so it reads as a plain square. See HUD 78.
  hideIcon?: boolean
}

// Fixed width of the status label gutter in "rows" layout, so each row's boxes
// start at the same x regardless of label length.
const ROWS_LABEL_WIDTH_PX = 96

// Gap between the label word and the first box in "rows" + labelHug mode, so the
// boxes start just to the right of the word (instead of an aligned gutter).
const ROWS_LABEL_HUG_GAP_PX = 10

const PoliciesSection = ({
  status,
  label,
  policies,
  onHoverPolicy,
  onHoverLabel,
  appearance,
  compact = false,
  reverse = false,
  layout = "columns",
  borderRadius,
  gap,
  strokeWidth,
  squareSize,
  boxWidth,
  labelWidth,
  shape,
  wrapBoxes = false,
  rowsLabelPosition = "left",
  labelHug = false,
  labelHugAlign = "start",
  statusIcon: StatusIcon,
  labelUppercase = true,
  labelFontSize,
  labelHugGapPx,
  overlapLabel = false,
  crowdedLabel,
  truncateLabelOnOverlap = false,
  fillPatternStyle,
  hideIcon,
}: PoliciesSectionProps) => {
  const { fillColor, borderColor, borderStyle, iconColor } = appearance

  // Tabs 23–27 (any square-highlight treatment): the hovered status LABEL
  // turns red while its section detail shows in the below-HUD map — color
  // only, no layout shift. With statusRowHover it also BOLDS (the labels are
  // left-anchored in their hug gutter, so the width change moves nothing).
  const hoverSwap = usePolicyHoverSwap()
  const layoutAnimationDisabled = usePolicyLayoutAnimationDisabled()
  const labelLit =
    hoverSwap?.squareHighlight !== undefined && hoverSwap.hoveredStatus === status
  // statusRowHover ("hud0-lean-status-underline" tab): the WHOLE row — label,
  // hug gap, the empty run beside the squares — is the label-hover target.
  // Guard: anything inside a policy square (data-policy-id, including its
  // invisible hit extension) belongs to the square's own hover; without the
  // guard, moving within a square would re-fire this and clobber it.
  const rowHoverProps =
    hoverSwap?.statusRowHover && onHoverLabel && layout === "rows"
      ? {
          onMouseOver: (e: React.MouseEvent) => {
            if ((e.target as HTMLElement).closest?.("[data-policy-id]")) return
            onHoverLabel()
          },
        }
      : undefined

  // Crowding check for `crowdedLabel`: compare the row's fixed-width content
  // (full label, hug gap, boxes) against the column width. The full label is
  // measured from a hidden copy so the check stays stable after the swap; the
  // column width comes from the section's parent (the panel container), which
  // keeps its w-full width even while an overflowing section grows past it.
  const sectionRef = useRef<HTMLDivElement>(null)
  const fullLabelMeasureRef = useRef<HTMLSpanElement>(null)
  const [isCrowded, setIsCrowded] = useState(false)
  const boxPitchWidth = boxWidth ?? squareSize ?? 20 // 20 mirrors PolicyItem's BOX_SIZE
  const boxGap = gap ?? BOX_GAP
  const hugGap = labelHugGapPx ?? ROWS_LABEL_HUG_GAP_PX
  // In truncate mode: how many characters of the label are clear of the boxes
  // (null = the full label fits). Mono font, so every character is fullWidth /
  // label.length wide, and a partially-touched character rounds DOWN (dropped).
  const [visibleLabelChars, setVisibleLabelChars] = useState<number | null>(null)
  useLayoutEffect(() => {
    if (crowdedLabel === undefined && !truncateLabelOnOverlap) return
    const column = sectionRef.current?.parentElement
    const fullLabelEl = fullLabelMeasureRef.current
    if (!column || !fullLabelEl) return
    const measure = () => {
      const boxesWidth = policies.length > 0
        ? policies.length * boxPitchWidth + (policies.length - 1) * boxGap
        : 0
      const fullLabelWidth = fullLabelEl.getBoundingClientRect().width
      const availableForLabel = column.getBoundingClientRect().width - hugGap - boxesWidth
      setIsCrowded(fullLabelWidth > availableForLabel + 0.5)
      if (truncateLabelOnOverlap) {
        setVisibleLabelChars(
          fullLabelWidth <= availableForLabel + 0.5
            ? null
            : Math.max(0, Math.floor(availableForLabel / (fullLabelWidth / label.length))),
        )
      }
    }
    measure()
    // A one-shot mount measure goes stale two ways (2026-07-09, seen on the
    // mobile dashboard): the mono font loading after mount widens the label,
    // and the column resizing (it's laid out inside the prewarmed-hidden
    // overlay). Re-measure on both.
    const observer = new ResizeObserver(measure)
    observer.observe(column)
    let cancelled = false
    document.fonts?.ready.then(() => { if (!cancelled) measure() })
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [crowdedLabel, truncateLabelOnOverlap, label, policies.length, boxPitchWidth, boxGap, hugGap])
  const displayLabel = truncateLabelOnOverlap && visibleLabelChars !== null
    ? label.slice(0, visibleLabelChars).trimEnd()
    : crowdedLabel !== undefined && isCrowded
      ? crowdedLabel
      : label
  const isRows = layout === "rows"
  const labelOnTop = isRows && rowsLabelPosition === "top"
  const labelOnRight = isRows && rowsLabelPosition === "right"
  // Label sits beside the boxes (left or right gutter) rather than above them.
  const labelOnSide = isRows && !labelOnTop
  const labelHugLeft = labelOnSide && !labelOnRight && labelHug
  const labelHugSide = labelOnSide && labelHug
  const sectionClassName = isRows
    ? (labelOnTop ? "flex flex-col gap-1" : `flex flex-row items-center${labelHug ? "" : " gap-3"}`)
    : compact
      ? "flex min-w-0 flex-1 flex-col gap-1 pb-4"
      : "flex flex-col gap-1 shrink-0 pb-4"
  const rowClassName = isRows
    ? [
        "flex items-center",
        wrapBoxes ? "flex-wrap" : "flex-nowrap",
        // min-w-0 lets the row shrink inside the column; [&>*]:shrink-0 keeps the
        // boxes at full size so the excess slides left over the label instead of
        // the boxes compressing to fit.
        overlapLabel ? "min-w-0 [&>*]:shrink-0" : "",
        labelOnTop
          ? "justify-start"
          : labelOnRight
            ? "justify-start flex-1"
            : labelHugLeft
              ? `${labelHugAlign === "end" ? "justify-end" : "justify-start"} flex-1`
              : "justify-end flex-1",
      ].join(" ")
    : compact
      ? "flex flex-wrap items-center"
      : "flex items-center flex-nowrap"

  const labelEl = (
    <div
      className={[
        "font-mono whitespace-nowrap tracking-wide",
        labelFontSize ? "" : "text-[9px]",
        labelUppercase ? "uppercase" : "",
        StatusIcon ? "inline-flex items-center" : "",
      ].filter(Boolean).join(" ")}
      onMouseEnter={onHoverLabel}
      style={{
        ...(labelLit ? { color: POLICY_LABEL_HOVER_COLOR } : {}),
        ...(labelLit && hoverSwap?.statusRowHover ? { fontWeight: 700 } : {}),
        ...(labelFontSize ? { fontSize: labelFontSize } : {}),
        ...(labelOnSide
          ? labelHug
            ? { flexShrink: 0 }
            : { width: labelWidth ?? ROWS_LABEL_WIDTH_PX, flexShrink: 0 }
          : {}),
      }}
    >
      {StatusIcon ? <StatusIcon size={14} /> : displayLabel}
      {/* Hidden copy of the FULL label, kept out of flow, so the crowding check
          can measure it even while a short/truncated label is displayed. */}
      {(crowdedLabel !== undefined || truncateLabelOnOverlap) && !StatusIcon && (
        <span ref={fullLabelMeasureRef} aria-hidden className="invisible absolute whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
  )

  const boxesEl = (
    <div className={rowClassName} style={{ gap: gap ?? BOX_GAP }}>
      {(reverse ? [...policies].reverse() : policies).map((policy) => (
        <PolicyItem
          key={policy.id}
          policy={policy}
          fillColor={fillColor}
          borderColor={borderColor}
          borderStyle={borderStyle}
          iconColor={iconColor}
          borderRadius={borderRadius}
          strokeWidth={strokeWidth}
          squareSize={squareSize}
          boxWidth={boxWidth}
          shape={shape}
          fillPatternStyle={fillPatternStyle}
          hideIcon={hideIcon}
          onHoverPolicy={onHoverPolicy}
        />
      ))}
      {policies.length === 0 && (
        <div className="text-[10px] font-mono opacity-40">—</div>
      )}
    </div>
  )

  return (
    <motion.div
      ref={sectionRef}
      // Layout tweens sit out while the host surface itself is moving (the
      // mobile sheet's rise) — see policyLayoutAnimationContext.
      layout={layoutAnimationDisabled ? false : "position"}
      transition={{ duration: 0.1, ease: "linear" }}
      className={sectionClassName}
      style={labelHugSide ? { gap: labelHugGapPx ?? ROWS_LABEL_HUG_GAP_PX } : undefined}
      {...rowHoverProps}
    >
      {/* "right" label: boxes hug the column's left edge, label sits to their right. */}
      {labelOnRight ? <>{boxesEl}{labelEl}</> : <>{labelEl}{boxesEl}</>}
    </motion.div>
  )
}

export default PoliciesSection
